import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { setPageMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import {
  Cube, ArrowRight, Calculator, Truck, Package, Warning
} from '@phosphor-icons/react';

const SHAPES = [
  { key: 'slab', label: 'Slab / Patio' },
  { key: 'footing', label: 'Footing / Trench' },
  { key: 'column', label: 'Round Column' },
  { key: 'steps', label: 'Steps' },
];

const BAG_YIELDS_CF = { 40: 0.30, 60: 0.45, 80: 0.60 };

const toFeet = (val, unit) => {
  const v = parseFloat(val);
  if (isNaN(v) || v < 0) return 0;
  return unit === 'in' ? v / 12 : unit === 'm' ? v * 3.28084 : unit === 'cm' ? v * 0.0328084 : v;
};

export default function ConcreteCalculator() {
  const [metric, setMetric] = useState(false);
  const [shape, setShape] = useState('slab');
  const [qty, setQty] = useState('1');
  const [waste, setWaste] = useState('10');
  const [f, setF] = useState({ length: '', width: '', thickness: '', diameter: '', height: '', steps: '3', rise: '7', run: '11', stepWidth: '36' });

  useEffect(() => {
    setPageMeta({
      title: 'Free Concrete Volume Calculator — Cubic Yards & Bags | ConstructHelp',
      description: 'Calculate how much concrete you need for slabs, footings, columns and steps. Get cubic yards, cubic meters and the exact number of 40/60/80 lb bags — with waste factor built in.',
      url: window.location.href,
    });
  }, []);

  const big = metric ? { u: 'm', label: 'm' } : { u: 'ft', label: 'ft' };
  const small = metric ? { u: 'cm', label: 'cm' } : { u: 'in', label: 'in' };

  // volume in cubic feet for ONE unit of the shape
  let volumeCf = 0;
  if (shape === 'slab') {
    volumeCf = toFeet(f.length, big.u) * toFeet(f.width, big.u) * toFeet(f.thickness, small.u);
  } else if (shape === 'footing') {
    volumeCf = toFeet(f.length, big.u) * toFeet(f.width, small.u) * toFeet(f.height, small.u);
  } else if (shape === 'column') {
    const r = toFeet(f.diameter, small.u) / 2;
    volumeCf = Math.PI * r * r * toFeet(f.height, big.u);
  } else if (shape === 'steps') {
    const n = Math.max(0, parseInt(f.steps) || 0);
    const riseFt = toFeet(f.rise, small.u);
    const runFt = toFeet(f.run, small.u);
    const wFt = toFeet(f.stepWidth, small.u);
    // each step i (1..n) is a block run deep, (i*rise) tall
    for (let i = 1; i <= n; i++) volumeCf += runFt * wFt * riseFt * i;
  }
  const count = Math.max(1, parseInt(qty) || 1);
  const wastePct = Math.min(50, Math.max(0, parseFloat(waste) || 0));
  const totalCf = volumeCf * count * (1 + wastePct / 100);
  const yards = totalCf / 27;
  const meters = totalCf * 0.0283168;
  const hasResult = totalCf > 0;

  const inputCls = 'w-full h-11 border-2 border-black dark:border-white px-3 bg-white dark:bg-neutral-800 dark:text-white font-mono';
  const labelCls = 'text-xs font-black uppercase tracking-wider text-neutral-500';

  const field = (key, label, unitLabel) => (
    <div>
      <label className={labelCls}>{label} ({unitLabel})</label>
      <input type="number" min="0" step="any" className={inputCls} value={f[key]}
        onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={`cc-${key}`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900" data-testid="concrete-calculator-page">
      {/* Hero */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="inline-flex items-center gap-2 bg-[#D95800] text-black text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-white shadow-[3px_3px_0px_#fff] mb-5">
            <Calculator size={14} weight="fill" /> Free Tool
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-tight mb-3">
            Concrete <span className="text-[#D95800]">Calculator</span>
          </h1>
          <p className="text-neutral-400 max-w-xl mx-auto text-base">
            Slabs, footings, columns and steps — get cubic yards, cubic meters, and exactly how many bags to buy. Waste factor included.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[5px_5px_0px_#000] dark:shadow-[5px_5px_0px_#2B4C6F] p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-black uppercase tracking-wide text-neutral-900 dark:text-white flex items-center gap-2">
              <Cube size={18} weight="fill" className="text-[#D95800]" /> Pour Details
            </h2>
            <button
              onClick={() => setMetric(!metric)}
              className="text-[10px] font-black uppercase tracking-wider px-2 py-1 border-2 border-black dark:border-white bg-neutral-100 dark:bg-neutral-800 dark:text-white"
              data-testid="cc-unit-toggle"
            >
              {metric ? 'Metric (m/cm)' : 'Imperial (ft/in)'} ⇄
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {SHAPES.map((s) => (
              <button key={s.key} onClick={() => setShape(s.key)}
                className={`px-3 py-1.5 text-xs font-black uppercase border-2 border-black ${shape === s.key ? 'bg-[#2B4C6F] text-white' : 'bg-white dark:bg-neutral-800 dark:text-white dark:border-white'}`}
                data-testid={`cc-shape-${s.key}`}>
                {s.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {shape === 'slab' && (<>
              {field('length', 'Length', big.label)}
              {field('width', 'Width', big.label)}
              {field('thickness', 'Thickness', small.label)}
            </>)}
            {shape === 'footing' && (<>
              {field('length', 'Length', big.label)}
              {field('width', 'Width', small.label)}
              {field('height', 'Depth', small.label)}
            </>)}
            {shape === 'column' && (<>
              {field('diameter', 'Diameter', small.label)}
              {field('height', 'Height', big.label)}
            </>)}
            {shape === 'steps' && (<>
              {field('steps', 'Number of steps', '#')}
              {field('stepWidth', 'Step width', small.label)}
              {field('rise', 'Rise per step', small.label)}
              {field('run', 'Run (tread depth)', small.label)}
            </>)}
            <div>
              <label className={labelCls}>Quantity (# of pours)</label>
              <input type="number" min="1" className={inputCls} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="cc-qty" />
            </div>
            <div>
              <label className={labelCls}>Waste factor (%)</label>
              <input type="number" min="0" max="50" className={inputCls} value={waste} onChange={(e) => setWaste(e.target.value)} data-testid="cc-waste" />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          <div className="bg-[#2B4C6F] border-2 border-black shadow-[5px_5px_0px_#000] p-5 text-white" data-testid="cc-results">
            <p className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-2">You Need</p>
            {!hasResult && <p className="text-white/70 text-sm">Enter your pour dimensions to see concrete volume and bag counts.</p>}
            {hasResult && (<>
              <p className="text-4xl font-black font-mono" data-testid="cc-yards">{yards.toFixed(2)} <span className="text-lg">yd³</span></p>
              <p className="text-white/70 font-mono text-sm mt-1" data-testid="cc-meters">
                = {meters.toFixed(2)} m³ · {totalCf.toFixed(1)} ft³ <span className="text-white/50">(incl. {wastePct}% waste)</span>
              </p>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-2 flex items-center gap-1"><Package size={12} weight="fill" /> Pre-Mix Bags</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[40, 60, 80].map((lb) => (
                    <div key={lb} className="bg-white/10 border border-white/20 py-2" data-testid={`cc-bags-${lb}`}>
                      <p className="text-xl font-black font-mono">{Math.ceil(totalCf / BAG_YIELDS_CF[lb])}</p>
                      <p className="text-[10px] uppercase text-white/60">{lb} lb bags</p>
                    </div>
                  ))}
                </div>
              </div>
              {yards >= 1 && (
                <p className="mt-4 text-xs text-white/80 flex items-start gap-2">
                  <Truck size={16} weight="fill" className="shrink-0 mt-0.5" />
                  <span>At {yards.toFixed(1)} yd³, ready-mix delivery is usually cheaper than bags — most trucks carry up to 10 yd³ and suppliers quote per yard.</span>
                </p>
              )}
            </>)}
          </div>

          <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white p-5">
            <h3 className="font-black uppercase tracking-wide text-neutral-900 dark:text-white text-sm mb-2">Pricing the whole job?</h3>
            <p className="text-xs text-neutral-500 mb-3">Our free AI Pricing Tool prices the full pour — labor, forms, rebar, finishing — calibrated to your local market. It even reads blueprint PDFs.</p>
            <Link to="/ai-pricing">
              <Button className="w-full h-10 bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase text-xs border-2 border-black rounded-none shadow-[3px_3px_0px_#000]" data-testid="cc-ai-pricing-cta">
                Price the Full Job Free <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* SEO content */}
      <div className="max-w-3xl mx-auto px-4 pb-14">
        <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white p-6 space-y-4 text-sm text-neutral-700 dark:text-neutral-300">
          <h2 className="text-base md:text-lg font-black uppercase text-neutral-900 dark:text-white">How much concrete do I need?</h2>
          <p>Multiply length × width × thickness (in feet) to get cubic feet, then divide by 27 for cubic yards. This calculator does the unit conversions for you and adds a waste factor — pros typically order <strong>5–10% extra</strong> to cover spillage, uneven subgrade, and over-excavation.</p>
          <p><strong>Bag yields:</strong> a 40 lb bag yields ≈ 0.30 ft³, a 60 lb bag ≈ 0.45 ft³, and an 80 lb bag ≈ 0.60 ft³ of mixed concrete. For pours over about one cubic yard (≈ 45 × 80 lb bags), ready-mix delivery usually wins on both cost and quality.</p>
          <p className="flex items-start gap-2 text-xs text-neutral-500"><Warning size={16} className="shrink-0 mt-0.5 text-[#D95800]" weight="fill" /> Estimates are for planning. Verify thickness and subgrade conditions on site, and check local code for required footing depths and reinforcement.</p>
        </div>
      </div>
    </div>
  );
}
